import { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import AccommodationPackageInclusions, { type PackageInclusionGroup } from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageInclusions'
import AccommodationPackageExtraInclusionsHeading from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageExtraInclusionsHeading'
import { getPackageInclusionsMinimumStay, type PackageInclusionsDisclosure } from 'components/AccommodationKit/AccommodationPackageSelection/packageInclusions'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import LoyaltyBenefitInfoIcon from 'loyalty/components/LoyaltyBenefitInfoIcon'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'

const Content = styled.div`
  position: relative;
  display: grid;
  gap: ${rem(8)};
`

interface Props {
  offer: App.HotelOffer
  showAll?: boolean
  disclosure?: PackageInclusionsDisclosure
  inclusions: Array<App.PackageInclusion>
  bonusInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
}

function FlashPackageSelectionInclusions({ offer, inclusions, bonusInclusions, luxPlusInclusions, showAll = false, disclosure }: Props) {
  const { duration } = useContext(OfferPageStateContext)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const loyaltyInclusions = useLoyaltyBenefitInclusions(offer)
  const groups = useMemo<Array<PackageInclusionGroup>>(() => [{
    category: 'lux-plus',
    heading: <LuxPlusLabelWithModal type="bonus-inclusions" inclusions={luxPlusInclusions} offer={offer} hideUpsellModal />,
    rows: (luxPlusEnabled ? luxPlusInclusions : EmptyArray).map(inclusion => ({ inclusion })),
  }, {
    category: 'night-extra',
    // Only rate-level Flash inclusions retain real bounds; package/rate-plan bounds can be synthetic.
    heading: <AccommodationPackageExtraInclusionsHeading category="night-extra" minimumStay={bonusInclusions.every(inclusion => inclusion.displayContext === 'Rate') ? getPackageInclusionsMinimumStay(bonusInclusions) : undefined} selectedDuration={duration} />,
    rows: bonusInclusions.map(inclusion => ({ inclusion })),
  }, {
    category: 'standard',
    rows: [
      ...inclusions.map(inclusion => ({ inclusion })),
      ...loyaltyInclusions.map(inclusion => ({ inclusion, endIcon: <LoyaltyBenefitInfoIcon inclusionId={inclusion.id} /> })),
    ],
  }], [bonusInclusions, duration, inclusions, loyaltyInclusions, luxPlusEnabled, luxPlusInclusions, offer])

  return <Content>
    <AccommodationPackageInclusions groups={groups} limit={8} showAll={showAll} disclosure={disclosure} />
    <ExperiencesPassportOfferSummary outOfFlowWhenEmpty />
  </Content>
}

export default FlashPackageSelectionInclusions
